import { useEffect, useRef, useState } from "react";
import { useProject } from "../project/ProjectContext";

/**
 * Project breadcrumb in the top header. Renders as `/ <project name>`
 * — the leading slash and space form the "Org / Project" breadcrumb
 * when this component sits to the right of `<OrgIndicator/>` in
 * `<Header/>`.
 *
 * Click opens a dropdown of authorized projects. Picking a different
 * project triggers `requestSwitch` → confirmation modal.
 */
export function ProjectIndicator() {
    const { authorizedProjects, selectedProjectId, requestSwitch, accessState } =
        useProject();
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open]);

    if (accessState !== "ready") {
        // Hide the indicator entirely when no project is selected. The org
        // indicator and the gate's picker / banner cover those states.
        return <span data-slot="project-indicator" data-testid="project-indicator-hidden" />;
    }

    const selected = authorizedProjects.find((p) => p.id === selectedProjectId);
    const label = selected?.name ?? "Unknown project";

    return (
        <div className="indicator-wrap" ref={ref} data-testid="project-indicator">
            <button
                type="button"
                className="chrome-indicator"
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label="Project menu"
                onClick={() => setOpen((prev) => !prev)}
                data-testid="project-indicator-button"
            >
                <i className="fa-solid fa-folder-tree" aria-hidden="true" />
                <span>{label}</span>
                <i className="fa-solid fa-chevron-down" aria-hidden="true" />
            </button>
            {open && (
                <div className="chrome-menu" role="menu">
                    {authorizedProjects.map((p) => {
                        const isCurrent = p.id === selectedProjectId;
                        return (
                            <button
                                key={p.id}
                                type="button"
                                role="menuitem"
                                aria-current={isCurrent}
                                data-current={isCurrent}
                                onClick={() => {
                                    setOpen(false);
                                    requestSwitch(p.id);
                                }}
                                data-testid={`project-option-${p.id}`}
                            >
                                {p.name}
                                {isCurrent && (
                                    <span aria-hidden="true" data-testid="current-marker">
                                        {" "}
                                        ✓
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
